import ModelFactory from 'business/models/ModelFactory';
import {
  ExtendedIngredientDTO,
  ImportIngredientsDTO,
  StringPropertyDTO,
  IngredientClient,
  IngredientDTO,
  IngredientIngredientTypeDTO,
  IngredientStockDTO,
  IngredientTypeDTO,
  LiquidClassDTO,
  PlateIngredientDTO,
  PrintBarcodeApiResponse,
  PrintBarcodeClient,
  IngredientTableRow,
  FileResponse,
  ResponseDTO,
  SeedNodeInfoDTO,
  VendorDTO,
  IngredientTypeModalDTO,
  IngredientDocumentDTO,
  SaveIngredientDocDTO,
  IngredientDocStockDTO,
  MergeIngredientDTO,
  DeleteIngredientResponse,
  ResponseWithValueDTOOfInteger,
  FavouriteIngredientStockDTO,
  PlateIngredientAndDrawingRequestDTO,
  PlateIngredientAndDrawingDTO,
  ProteinFormulationIngredientTypeDTO,
  ConflictResolutionType,
} from 'business/models/Models';

function GetPlateIngredientsService(plateId): Promise<PlateIngredientDTO> {
  const ing: IngredientClient = ModelFactory(IngredientClient, false, true);

  const root = ing.getPlateIngredients(plateId);

  return root;
}

function printIngredientBarcode(
  ingredientStockId: number,
): Promise<PrintBarcodeApiResponse> {
  const client: PrintBarcodeClient = ModelFactory(PrintBarcodeClient, false, true);
  return client.performIngredientBarcodPrinting(ingredientStockId);
}
const GetAllPlatesIngredients = (
  requestData: PlateIngredientAndDrawingRequestDTO,
): Promise<PlateIngredientAndDrawingDTO[]> => {
  const ing: IngredientClient = ModelFactory(IngredientClient, false, true);
  return ing.getAllPlatesIngredients(requestData);
};

function getConflictedIngredientsList(
  ingredientsXml: string,
): Promise<ImportIngredientsDTO> {
  const client: IngredientClient = ModelFactory(IngredientClient);
  const ingredientStr = new StringPropertyDTO();
  ingredientStr.name = ingredientsXml;
  return client.getConflictedIngredientsList(ingredientStr);
}
function GetSeedNodeInfo(treeNodeId): Promise<SeedNodeInfoDTO> {
  const ing: IngredientClient = ModelFactory(IngredientClient, false, true);

  const res = ing.getSeedNodeInfo(treeNodeId);

  return res;
}
function ValidateIngredient(
  ingredient: ExtendedIngredientDTO,
  resolutionType: ConflictResolutionType,
): Promise<StringPropertyDTO> {
  const ing: IngredientClient = ModelFactory(IngredientClient, false, true);
  const res = ing.validateIngredient(ingredient, resolutionType);
  return res;
}
function SaveImportedIngredients(
  importIngredientsDto: ImportIngredientsDTO,
): Promise<boolean> {
  const ing: IngredientClient = ModelFactory(IngredientClient, false, true);
  const res = ing.saveImportedIngredients(importIngredientsDto);
  return res;
}

function mergeIngredients(
  mergeIngredientDTO: MergeIngredientDTO,
): Promise<{ [key: string]: IngredientDocStockDTO[] }> {
  const ing: IngredientClient = ModelFactory(IngredientClient, false, true);
  return ing.mergeIngredient(mergeIngredientDTO);
}

function getIngredients(): Promise<IngredientDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getIngredients();
}

const getIngredientsByIds = (ids: number[]): Promise<ExtendedIngredientDTO[]> => {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getIngredientsByIds(ids);
};

function getIngredientStocks(): Promise<IngredientStockDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getIngredientStocks();
}

function getIngredientIngredientTypes(): Promise<IngredientIngredientTypeDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getIngredientIngredientTypes();
}

function getIngredientTypes(): Promise<IngredientTypeDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getIngredientTypes();
}

function getLiquidClasses(): Promise<LiquidClassDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getLiquidClasses();
}

function getVendors(): Promise<VendorDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getVendors();
}

function getProteinTypes(): Promise<ProteinFormulationIngredientTypeDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getProteinTypes();
}

function getIngredientById(id: number): Promise<IngredientDocumentDTO> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getIngredientById(id);
}
function saveIngredientTypeModal(
  ingredientTypeData: IngredientTypeModalDTO,
): Promise<ResponseDTO> {
  const client: IngredientClient = ModelFactory(IngredientClient);
  return client.saveIngredientType(ingredientTypeData);
}

function saveVendor(vendors: VendorDTO[]): Promise<ResponseDTO> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.saveVendor(vendors);
}

function saveProteinFormulationIngredientTypes(
  proteinTypes: ProteinFormulationIngredientTypeDTO[],
): Promise<ResponseDTO> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.saveProteinType(proteinTypes);
}

function saveLiquidClass(liquidClasses: LiquidClassDTO[]): Promise<ResponseDTO> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.saveLiquidClass(liquidClasses);
}

function ingredientsExportToExcel(
  ingredients: IngredientTableRow[],
): Promise<FileResponse> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.exportIngredientsToExcel(ingredients);
}

function ingredientsExportToText(
  ingredients: IngredientTableRow[],
): Promise<FileResponse> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.exportIngredientsToText(ingredients);
}

function ingredientsExportToXML(
  ingredients: IngredientTableRow[],
): Promise<FileResponse> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.exportIngredientsToXML(ingredients);
}

function ingredientsExportToXMLSpreadsheet(
  ingredients: IngredientTableRow[],
): Promise<FileResponse> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.exportIngredientsToXMLSpreadsheet(ingredients);
}

function saveIngredient(
  ingredient: SaveIngredientDocDTO,
): Promise<ResponseWithValueDTOOfInteger> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.save(ingredient);
}

function getStocksByIngredientId(ingredientId: number): Promise<IngredientDocStockDTO[]> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.getStocksByIngredient(ingredientId);
}

function deleteIngredients(ingredientIds: number[]): Promise<DeleteIngredientResponse> {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return client.deleteIngredient(ingredientIds);
}

async function updateFavouriteIngStock(favIngStock: FavouriteIngredientStockDTO) {
  const client: IngredientClient = ModelFactory(IngredientClient, false, true);
  return await client.updateFavouriteIngredientStock(favIngStock);
}
export {
  GetPlateIngredientsService,
  printIngredientBarcode,
  GetSeedNodeInfo,
  getIngredients,
  getIngredientStocks,
  getIngredientIngredientTypes,
  getIngredientTypes,
  getConflictedIngredientsList,
  ValidateIngredient,
  SaveImportedIngredients,
  getVendors,
  getIngredientById,
  getLiquidClasses,
  saveIngredientTypeModal,
  saveVendor,
  saveLiquidClass,
  getIngredientsByIds,
  saveIngredient,
  ingredientsExportToExcel,
  ingredientsExportToText,
  ingredientsExportToXML,
  ingredientsExportToXMLSpreadsheet,
  getStocksByIngredientId,
  mergeIngredients,
  deleteIngredients,
  updateFavouriteIngStock,
  GetAllPlatesIngredients,
  getProteinTypes,
  saveProteinFormulationIngredientTypes,
};
